/**
 * 비회원 스캔 체험 (기획확정안 §4.1).
 *
 * 규칙 (문서 그대로):
 *  - 가입 전 **평생 5회**만 스캔 가능하며 초기화하지 않는다
 *  - 카드명·언어판·세트·카드번호 기준으로 **기본 카드가 식별되어 결과가 표시되면 1회 차감**
 *  - 세부 버전이나 등급을 선택하기 전이라도 기본 카드 인식에 성공하면 차감
 *  - 같은 인식 결과 안에서 후보·버전·등급을 수정해도 **추가 차감하지 않음**
 *  - 같은 카드가 카메라에서 즉시 중복 인식되면 **한 건으로 처리**
 *  - 5회 소진 후 추가 인식을 막고 간편로그인 화면을 제시
 *  - 인식 실패·빛 반사·흔들림·시스템 오류로 식별 실패 시 **차감하지 않음** (§3.5)
 *
 * 로그인 회원은 무제한이므로 이 스토어는 비회원 경로에서만 호출한다.
 * 최근 5장 임시 기록은 로그인 시 계정으로 이전한다 (§4.3) — transferToAccount().
 */
import { create } from "zustand";

export const GUEST_SCAN_LIMIT = 5;
/** 같은 카드 즉시 중복 인식으로 볼 시간 창 (§4.1) */
const DUPLICATE_WINDOW_MS = 60_000;
/** 비회원 임시 스캔 기록 보관 장수 (§4 표: "최근 5장 임시 기록") */
const GUEST_HISTORY_MAX = 5;

const COUNT_KEY = "peeka.guestScanCount";
const HISTORY_KEY = "peeka.guestScanHistory";

export interface GuestScanEntry {
  cardUid: string;
  scannedAt: number;
}

function readCount(): number {
  try {
    const raw = localStorage.getItem(COUNT_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function readHistory(): GuestScanEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as GuestScanEntry[]) : [];
  } catch {
    return [];
  }
}

function persist(count: number, history: GuestScanEntry[]): void {
  try {
    localStorage.setItem(COUNT_KEY, String(count));
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch {
    /* private mode — 저장 실패 시 이번 세션 한정으로만 동작 */
  }
}

/** consumeScan 결과 — 호출부가 UI 분기에 사용 */
export type ConsumeResult =
  /** 차감됨 (또는 중복이라 차감 없이 통과) — 결과 화면으로 진행 */
  | { ok: true; duplicated: boolean; remaining: number }
  /** 한도 소진 — 간편로그인 화면 제시 */
  | { ok: false; reason: "limit" };

interface GuestScanStore {
  count: number;
  history: GuestScanEntry[];
  /** 남은 횟수 */
  remaining: () => number;
  /** 한도 소진 여부 */
  exhausted: () => boolean;
  /**
   * 기본 카드 식별 **성공** 시에만 호출한다 (§4.1).
   * 인식 실패·오류 경로에서는 호출하지 말 것 — 차감하면 안 된다 (§3.5).
   */
  consumeScan: (cardUid: string) => ConsumeResult;
  /** 로그인 성공 시 계정으로 이전 (§4.3) 후 비회원 기록 정리 */
  transferToAccount: () => GuestScanEntry[];
}

export const useGuestScanStore = create<GuestScanStore>()((set, get) => ({
  count: readCount(),
  history: readHistory(),

  remaining: () => Math.max(0, GUEST_SCAN_LIMIT - get().count),
  exhausted: () => get().count >= GUEST_SCAN_LIMIT,

  consumeScan: (cardUid) => {
    const { count, history } = get();
    const now = Date.now();

    // 같은 카드 즉시 중복 인식 → 한 건으로 처리 (차감 없음)
    const last = history[0];
    if (last && last.cardUid === cardUid && now - last.scannedAt < DUPLICATE_WINDOW_MS) {
      return { ok: true, duplicated: true, remaining: Math.max(0, GUEST_SCAN_LIMIT - count) };
    }

    if (count >= GUEST_SCAN_LIMIT) return { ok: false, reason: "limit" };

    const nextCount = count + 1;
    const nextHistory = [{ cardUid, scannedAt: now }, ...history].slice(0, GUEST_HISTORY_MAX);
    set({ count: nextCount, history: nextHistory });
    persist(nextCount, nextHistory);
    return { ok: true, duplicated: false, remaining: GUEST_SCAN_LIMIT - nextCount };
  },

  transferToAccount: () => {
    const { history } = get();
    set({ history: [] });
    // count 는 남겨둔다 — 로그아웃 후 다시 비회원이 되어도 "평생 5회"가 초기화되면 안 된다.
    persist(get().count, []);
    return history;
  },
}));
